<template>
  <section class="main-content">
    <div class="two-thirds-col-wrapper-checkout">
      <div class="col-inner checkout-blade">
        <h1>Checkout</h1>

        <form
          id="checkout-form"
          class="checkout-page__form"
          @submit.prevent="submitForm"
        >
          <section
            id="account-information"
            class="checkout-page__form-container"
          >
            <div :class="!authUser ? 'form-item' : ''">
              <h3>Account Information</h3>
              <p v-if="authUser">
                You are logged in as
                <strong>
                  {{ authUser.email }}
                </strong>
                . Not you?
                <a href="/logout">Click here to logout</a>
                .
              </p>
              <p v-else>
                You must create an account in order to make a purchase. Already
                have an account?
                <a href="/login?redirect=checkout">Click here to sign in</a>
                .
              </p>
            </div>
            <template v-if="!authUser">
              <PasswordComponent
                @email-update="form.account.email = $event"
                @password-update="form.account.password = $event"
                @password-confirm-update="
                  form.account.passwordConfirmation = $event
                "
                :email_error_message="errors.account.email"
                :password_error_message="errors.account.password"
                :password_confirm_error_message="
                  errors.account.passwordConfirmation
                "
                :show_submit_button="false"
                :horizontal_view="true"
                :show_email_field="true"
                :show_cancel_button="false"
                :enable_previous_password_check="false"
                email_label="*Email"
                password_label="*Password"
                password_confirm_label="*Password confirmation"
              />
            </template>
          </section>

          <section
            id="customer-information"
            class="checkout-page__form-container"
          >
            <div class="form-item" v-if="!authUser">
              <h3>Customer Information</h3>
              <p>Please enter your information.</p>
            </div>

            <section class="checkout-page__container-row">
              <FormInput
                v-model="form.customer.firstName"
                id="customer-information-first-name"
                name="customer-information-first-name"
                type="text"
                autofocus
                label-text="*First Name"
                required
                :error-message="errors.customer.firstName"
                autocomplete="given-name"
              />
              <FormInput
                v-model="form.customer.lastName"
                id="customer-information-last-name"
                name="customer-information-last-name"
                type="text"
                label-text="*Last Name"
                required
                :error-message="errors.customer.lastName"
                autocomplete="family-name"
              />
            </section>

            <section class="checkout-page__container-row">
              <FormInput
                v-model="form.customer.company"
                id="customer-information-company"
                name="customer-information-company"
                type="text"
                autofocus
                label-text="Company"
                :error-message="errors.customer.company"
                autocomplete="organization"
              />
              <FormInput
                v-model="form.customer.phone"
                id="customer-information-phone"
                name="customer-information-phone"
                type="text"
                label-text="Phone"
                :error-message="errors.customer.phone"
                autocomplete="tel"
              />
            </section>

            <section class="checkout-page__container-row">
              <FormInput
                v-model="form.customer.address"
                id="customer-information-address"
                name="customer-information-address"
                type="text"
                label-text="*Address"
                required
                :error-message="errors.customer.address"
                autocomplete="customer address-line1"
              />
              <FormInput
                v-model="form.customer.address2"
                id="customer-information-address2"
                name="customer-information-address2"
                type="text"
                label-text="Address 2"
                :error-message="errors.customer.address2"
                autocomplete="customer address-line2"
              />
            </section>

            <section class="checkout-page__container-row">
              <FormCountry
                v-model="form.customer.country"
                id="customer-information-country"
                name="customer-information-country"
                label-text="*Country"
                required
                :error-message="errors.customer.country"
                autocomplete="customer country"
              />
              <FormState
                v-model="form.customer.state"
                id="customer-information-state"
                name="customer-information-state"
                label-text="*State / Region"
                required
                :error-message="errors.customer.state"
                autocomplete="customer address-level1"
                :country="form.customer.country"
              />
            </section>

            <section class="checkout-page__container-row">
              <FormInput
                v-model="form.customer.city"
                id="customer-information-city"
                name="customer-information-city"
                type="text"
                label-text="*City"
                required
                :error-message="errors.customer.city"
                autocomplete="customer address-level2"
              />
              <FormInput
                v-model="form.customer.postcode"
                id="customer-information-postcode"
                name="customer-information-postcode"
                type="text"
                label-text="*Zip / Postal Code"
                required
                :error-message="errors.customer.postcode"
                autocomplete="customer postal-code"
              />
            </section>

            <BaseCheckbox
              v-model="form.billing.isSameAsShipping"
              id="billing-information-is-same-as-shipping"
              name="billing-information-is-same-as-shipping"
              style="font-weight: 400"
            >
              <span v-if="authUser && authUser.user_address">
                *Use my account information for billing
              </span>
              <span v-else>*Billing is same as shipping</span>
            </BaseCheckbox>
          </section>

          <section
            id="billing-information"
            class="checkout-page__form-container checkout-page__form-container--hideable"
            :class="{
              'checkout-page__form-container--hidden':
                (authUser &&
                  !authUser.user_address &&
                  form.billing.isSameAsShipping) ||
                (!authUser && form.billing.isSameAsShipping),
            }"
          >
            <div class="form-item">
              <h3>Billing Information</h3>
              <p>
                Please enter the billing information associated with your
                payment information.
              </p>
            </div>

            <section class="checkout-page__container-row">
              <FormInput
                v-model="form.billing.firstName"
                id="billing-information-first-name"
                name="billing-information-first-name"
                type="text"
                autofocus
                label-text="*First Name"
                :required="!form.billing.isSameAsShipping"
                :error-message="errors.billing.firstName"
                :autocomplete="
                  form.billing.isSameAsShipping ? 'off' : 'given-name'
                "
                :role="
                  form.billing.isSameAsShipping ? 'presentation' : 'textbox'
                "
              />
              <FormInput
                v-model="form.billing.lastName"
                id="billing-information-last-name"
                name="billing-information-last-name"
                type="text"
                label-text="*Last Name"
                :required="!form.billing.isSameAsShipping"
                :error-message="errors.billing.lastName"
                :autocomplete="
                  form.billing.isSameAsShipping ? 'off' : 'family-name'
                "
                :role="
                  form.billing.isSameAsShipping ? 'presentation' : 'textbox'
                "
              />
            </section>

            <section class="checkout-page__container-row">
              <FormInput
                v-model="form.billing.address"
                id="billing-information-address"
                name="billing-information-address"
                type="text"
                label-text="*Address"
                :required="!form.billing.isSameAsShipping"
                :error-message="errors.billing.address"
                :autocomplete="
                  form.billing.isSameAsShipping
                    ? 'off'
                    : 'billing address-line1'
                "
                :role="
                  form.billing.isSameAsShipping ? 'presentation' : 'textbox'
                "
              />
              <FormInput
                v-model="form.billing.address2"
                id="billing-information-address2"
                name="billing-information-address2"
                type="text"
                label-text="Address 2"
                :error-message="errors.billing.address2"
                :autocomplete="
                  form.billing.isSameAsShipping
                    ? 'off'
                    : 'billing address-line2'
                "
                :role="
                  form.billing.isSameAsShipping ? 'presentation' : 'textbox'
                "
              />
            </section>

            <section class="checkout-page__container-row">
              <FormCountry
                v-model="form.billing.country"
                id="billing-information-country"
                name="billing-information-country"
                label-text="*Country"
                :required="!form.billing.isSameAsShipping"
                :error-message="errors.billing.country"
                :autocomplete="
                  form.billing.isSameAsShipping ? 'off' : 'billing country'
                "
                :role="
                  form.billing.isSameAsShipping ? 'presentation' : 'textbox'
                "
              />
              <FormState
                v-model="form.billing.state"
                id="billing-information-state"
                name="billing-information-state"
                label-text="*State / Region"
                :required="!form.billing.isSameAsShipping"
                :error-message="errors.billing.state"
                :autocomplete="
                  form.billing.isSameAsShipping
                    ? 'off'
                    : 'billing address-level1'
                "
                :role="
                  form.billing.isSameAsShipping ? 'presentation' : 'textbox'
                "
                :country="form.billing.country"
              />
            </section>

            <section class="checkout-page__container-row">
              <FormInput
                v-model="form.billing.city"
                id="billing-information-city"
                name="billing-information-city"
                type="text"
                label-text="*City"
                :required="!form.billing.isSameAsShipping"
                :error-message="errors.billing.address"
                :autocomplete="
                  form.billing.isSameAsShipping
                    ? 'off'
                    : 'billing address-level2'
                "
                :role="
                  form.billing.isSameAsShipping ? 'presentation' : 'textbox'
                "
              />
              <FormInput
                v-model="form.billing.postcode"
                id="billing-information-postcode"
                name="billing-information-postcode"
                type="text"
                label-text="*Zip / Postal Code"
                :required="!form.billing.isSameAsShipping"
                :error-message="errors.billing.postcode"
                :autocomplete="
                  form.billing.isSameAsShipping ? 'off' : 'billing postal-code'
                "
                :role="
                  form.billing.isSameAsShipping ? 'presentation' : 'textbox'
                "
              />
            </section>
          </section>

          <section
            id="payment-information"
            class="checkout-page__form-container"
          >
            <h3>Payment</h3>
            <FormSelect
              v-model="form.payment.paymentType"
              id="payment-type"
              name="payment-type"
              label-text="*Select payment type"
              :options="[
                { value: 'cc', label: 'Credit card' },
                { value: 'checking', label: 'E-Check - Personal checking' },
                {
                  value: 'businessChecking',
                  label: 'E-Check - Business checking',
                },
                { value: 'savings', label: 'E-Check - Savings' },
              ]"
              autocomplete="off"
            />

            <section
              v-if="form.payment.paymentType === 'cc'"
              id="credit-card-information"
              class="checkout-page__form-container"
            >
              <FormInput
                v-model="form.payment.cardNumber"
                id="payment-credit-card-number"
                name="payment-credit-card-number"
                type="number"
                autofocus
                label-text="*Credit card number"
                required
                minlength="8"
                :error-message="errors.payment.cardNumber"
                @blur="check_bin(form.payment.cardNumber)"
                autocomplete="cc-number"
              />
              <section class="checkout-page__container-row">
                <FormSelect
                  v-model="form.payment.expiryMonth"
                  id="payment-credit-card-expiry-month"
                  name="payment-credit-card-expiry-month"
                  label-text="*Expiration Month"
                  :options="expiryMonthOptions"
                  :error-message="errors.payment.expiryMonth"
                  autocomplete="cc-exp-month"
                  style="width: 90%"
                />
                <FormSelect
                  v-model="form.payment.expiryYear"
                  id="payment-credit-card-expiry-year"
                  name="payment-credit-card-expiry-year"
                  label-text="*Expiration Year"
                  :options="expiryYearOptions"
                  autocomplete="cc-exp-year"
                  :error-message="errors.payment.expiryYear"
                />
                <FormInput
                  v-model="form.payment.cvv"
                  id="payment-credit-card-cvv"
                  name="payment-credit-card-cvv"
                  type="text"
                  autofocus
                  label-text="*CVV"
                  required
                  :error-message="errors.payment.cvv"
                  autocomplete="cc-csc"
                />
              </section>
            </section>
            <section
              v-else
              id="check-information"
              class="checkout-page__form-container"
            >
              <section class="checkout-page__container-row">
                <FormInput
                  v-model="form.payment.routingNumber"
                  id="payment-check-routing-number"
                  name="payment-check-routing-number"
                  type="text"
                  autofocus
                  label-text="*Routing number"
                  required
                  :error-message="errors.payment.routingNumber"
                  autocomplete="off"
                />
                <FormInput
                  v-model="form.payment.accountNumber"
                  id="payment-check-account-number"
                  name="payment-check-account-number"
                  type="text"
                  autofocus
                  label-text="*Account number"
                  required
                  :error-message="errors.payment.accountNumber"
                  autocomplete="off"
                />
              </section>
              <FormInput
                v-model="form.payment.accountName"
                id="payment-check-account-name"
                name="payment-check-account-name"
                type="text"
                autofocus
                label-text="*Account name"
                required
                :error-message="errors.payment.accountName"
                autocomplete="off"
              />
            </section>
          </section>
          <section class="submit-container">
            <BaseButton
              type="submit"
              color="primary"
              class="checkout-page__button"
              :disabled="isSubmitting"
              @click="validate()"
            >
              PAY NOW
            </BaseButton>
            <section class="submit-container__terms">
              <BaseCheckbox
                v-model="form.meta.hasConfirmedTerms"
                id="meta-has-confirmed-terms"
                name="meta-has-confirmed-terms"
                style="font-weight: 400"
                :has-error="!!errors.meta.hasConfirmedTerms"
              >
                <span>
                  *By clicking here, I state that I have read and understood the
                  <a
                    href="https://promotions.itreconomics.com/en/master-services-agreement"
                    target="_blank"
                  >
                    terms and conditions
                  </a>
                  .
                </span>
              </BaseCheckbox>
              <p v-if="errors.meta.hasConfirmedTerms" class="terms-error">
                {{ errors.meta.hasConfirmedTerms }}
              </p>
            </section>
          </section>
          <p class="warning" v-if="errors.message">{{ errors.message }}</p>
          <div class="place-order-ssl-icons">
            <img
              id="placeorder_row"
              src="/images/ssl/sectigo_trust_seal_md.png"
              alt="Securd by Sectigo"
              title="Securd by Sectigo"
              class="form-control-static"
            />
          </div>
        </form>
      </div>

      <div class="col-inner">
        <div class="order-summary-box-area">
          <div class="order-summary-box">
            <h4>Order Summary</h4>
            <div
              v-for="item in cartStore.itemsGetter"
              :key="`order-summary-item-${item.id}`"
              class="summary-product"
            >
              <div>
                <img
                  :src="
                    item.attributes?.images?.thumb
                      ? item.attributes.images.thumb
                      : '/images/retail-icon.png'
                  "
                  :alt="
                    item.attributes?.images?.thumb
                      ? `${item.name} Thumbnail`
                      : 'Default Product Thumbnail'
                  "
                  class="img-responsive"
                />
              </div>
              <div v-if="item.attributes.type == 'Invoice'">
                <h5>
                  <span class="item-name">Invoice Number:</span>
                  <span style="font-weight: normal">
                    {{ item.attributes.invoice_number }}
                  </span>
                </h5>
                <h5>
                  <span class="item-name">Due Date:</span>
                  <span style="font-weight: normal">
                    {{ item.attributes.due_date }}
                  </span>
                </h5>
              </div>
              <div v-else>
                <h5 class="item-name">
                  {{ item.name }}
                </h5>
                <p>
                  <span
                    v-if="
                      item.attributes.billing_period === 'monthly' ||
                      item.attributes.billing_period === 'annually'
                    "
                  >
                    Auto Renews
                    {{
                      item.attributes.billing_period.charAt(0).toUpperCase() +
                      item.attributes.billing_period.slice(1)
                    }}
                    <br />
                  </span>
                  Qty: {{ item.quantity }}
                  <br />
                  Each: ${{ item.price }}
                  <br />
                </p>
              </div>
            </div>
            <CartPromoCodes v-if="!cartStore.containsInvoiceItems" />
            <CartSummary
              :is-using-credit-card="
                cartStore.total > 0 &&
                form.payment.paymentType === 'cc' &&
                form.payment.isCreditCard
              "
              :paymentType="form.payment.paymentType"
              :shipping-rate="form.mail.shippingRate"
            />
          </div>
          <p class="place-order-ssl-icons margin-top--half">
            <img
              src="/images/ssl/sectigo_trust_seal_lg.png"
              alt="Securd by Sectigo"
              title="Securd by Sectigo"
            />
          </p>
        </div>
      </div>
    </div>
  </section>

  <div v-if="isSubmitting" class="checkout-page__loading">
    <p><img src="/images/loading.png" alt="Loading" /></p>
    <p>Please wait while we process your order request.</p>
  </div>

  <BaseModal v-if="showModal" rounded dropShadow size="sm" :noPadding="true">
    <div class="subscription-modal">
      <h2 class="subscription-modal__title">
        Let's Upgrade Your Subscription!
      </h2>
      <p class="subscription-modal__subtitle">
        When you upgrade your subscription, you'll receive instant access to
        your new subscription components, and your bundle will automatically
        renew on {{ renewelDate }}.
        <br />
        <br />
        Your first bundle billing will be prorated to include your current
        subscription, as seen in the order summary.
      </p>
      <div class="subscription-modal__button-container">
        <a
          @click="showModal = !showModal"
          class="button-green subscription-modal__button"
        >
          continue
        </a>
        <a
          @click="backToCart()"
          class="button-charcoal subscription-modal__button"
        >
          back to cart
        </a>
      </div>
    </div>
  </BaseModal>
</template>

<script>
  import axios from 'axios';
  import { useCartStore } from '~/stores/cart';
  import FormInput from '~/components/form/FormInput.vue';
  import FormSelect from '~/components/form/FormSelect.vue';
  import FormCountry from '~/components/form/FormCountry.vue';
  import FormState from '~/components/form/FormState.vue';
  import BaseCheckbox from '~/components/base/BaseCheckbox.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import CartPromoCodes from '~/components/cart/CartPromoCodesComponent.vue';
  import CartSummary from '~/components/cart/CartSummaryComponent.vue';
  import BaseModal from '~/components/base/BaseModal.vue';
  import PasswordComponent from '~/components/user/PasswordComponent.vue';

  export default {
    name: 'CheckoutPage',
    components: {
      FormInput,
      FormSelect,
      FormCountry,
      FormState,
      BaseCheckbox,
      BaseButton,
      CartPromoCodes,
      CartSummary,
      BaseModal,
      PasswordComponent,
    },
    props: {
      authUser: {
        type: Object,
        required: false,
        default: null,
      },
    },
    setup() {
      const cartStore = useCartStore();
      return { cartStore };
    },
    data: () => ({
      isSubmitting: false,
      form: {
        meta: {
          isShippingRequired: false,
          isPaymentRequired: false,
          hasConfirmedTerms: false,
        },
        account: {
          email: '',
          password: '',
          passwordConfirmation: '',
        },
        customer: {
          firstName: '',
          lastName: '',
          address: '',
          address2: '',
          city: '',
          country: '',
          postcode: '',
          state: '',
        },
        billing: {
          isSameAsShipping: false,
          firstName: '',
          lastName: '',
          address: '',
          address2: '',
          city: '',
          country: '',
          postcode: '',
          state: '',
        },
        mail: {
          mailType: 'priority',
          zipCode: '',
          shippingRate: 0,
        },
        payment: {
          paymentType: 'cc',
          cardNumber: '',
          expiryMonth: '01',
          expiryYear: '',
          cvv: '',
          isCreditCard: false,
          routingNumber: '',
          accountNumber: '',
          accountName: '',
        },
      },
      errors: {
        message: '',
        meta: {
          hasConfirmedTerms: '',
        },
        account: {
          email: '',
          password: '',
          passwordConfirmation: '',
        },
        customer: {
          firstName: '',
          lastName: '',
          company: '',
          phone: '',
          address: '',
          address2: '',
          city: '',
          country: '',
          postcode: '',
          state: '',
        },
        billing: {
          firstName: '',
          lastName: '',
          address: '',
          address2: '',
          city: '',
          country: '',
          postcode: '',
          state: '',
        },
        mail: {
          mailType: '',
          zipCode: '',
          shippingRate: '',
        },
        payment: {
          paymentType: '',
          cardNumber: '',
          expiryMonth: '',
          expiryYear: '',
          cvv: '',
          routingNumber: '',
          accountNumber: '',
          accountName: '',
        },
      },
      expiryMonthOptions: [
        { value: '01', label: 'January' },
        { value: '02', label: 'February' },
        { value: '03', label: 'March' },
        { value: '04', label: 'April' },
        { value: '05', label: 'May' },
        { value: '06', label: 'June' },
        { value: '07', label: 'July' },
        { value: '08', label: 'August' },
        { value: '09', label: 'September' },
        { value: '10', label: 'October' },
        { value: '11', label: 'November' },
        { value: '12', label: 'December' },
      ],
      expiryYearOptions: [],
      showModal: false,
      renewelDate: null,
    }),
    watch: {
      'form.billing.isSameAsShipping'() {
        this.form.billing.firstName = this.form.customer.firstName;
        this.form.billing.lastName = this.form.customer.lastName;
        this.form.billing.address = this.form.customer.address;
        this.form.billing.address2 = this.form.customer.address2;
        this.form.billing.city = this.form.customer.city;
        this.form.billing.country = this.form.customer.country;
        this.form.billing.postcode = this.form.customer.postcode;
        this.form.billing.state = this.form.customer.state;
      },
      'form.payment.expiryMonth'() {
        this.errors.payment.expiryMonth = '';
      },
      'form.payment.expiryYear'() {
        this.errors.payment.expiryYear = '';
      },
      'form.customer.country'() {
        this.errors.customer.country = '';
      },
      'form.customer.state'() {
        this.errors.customer.state = '';
      },
      'form.billing.country'() {
        this.errors.billing.country = '';
      },
      'form.billing.state'() {
        this.errors.billing.state = '';
      },
      'form.meta.hasConfirmedTerms'(value) {
        if (value) {
          this.errors.meta.hasConfirmedTerms = '';
        }
      },
    },
    async created() {
      this.setFormMeta();

      if (this.authUser) {
        this.form.customer.firstName = this.authUser.first_name;
        this.form.customer.lastName = this.authUser.last_name;
        this.form.customer.company = this.authUser.company;
        this.form.customer.phone = this.authUser.phone;
        await this.cartStore.fetchCart();
        this.showModal = await this.cartStore.checkCart();
        if (this.showModal) {
          let items = this.cartStore.itemsGetter;
          items.forEach((item) => {
            if (item.attributes.bundle_calculator) {
              let date = new Date(
                item.attributes.bundle_calculator.next_renewal
              );
              let adjustedDate = new Date(
                date.getTime() + date.getTimezoneOffset() * 60000
              );
              this.renewelDate = `${adjustedDate.getMonth() + 1}/${adjustedDate.getDate()}/${adjustedDate.getFullYear()}`;
            }
          });
        }
        if (this.authUser.user_address) {
          this.form.customer.address = this.authUser.user_address.address;
          this.form.customer.address2 = this.authUser.user_address.address_2;
          this.form.customer.city = this.authUser.user_address.city;
          this.form.customer.state = this.authUser.user_address.state;
          this.form.customer.country = this.authUser.user_address.country;
          this.form.customer.postcode = this.authUser.user_address.postcode;
          this.form.mail.zipCode = this.authUser.user_address.postcode;
        }
      }

      const currentYear = new Date().getFullYear();
      for (let yearIndex = 0; yearIndex < 10; yearIndex++) {
        this.expiryYearOptions.push({
          value: currentYear + yearIndex,
          label: currentYear + yearIndex,
        });
      }
      this.form.payment.expiryYear = currentYear;

      this.check_bin(this.form.payment.cardNumber);
    },

    methods: {
      setFormMeta() {
        this.form.meta.isShippingRequired = this.cartStore.isShippable;
        this.form.meta.isPaymentRequired =
          this.cartStore.total > 0 || this.cartStore.discountExceedsTotal;
      },
      validate() {
        if (!this.form.meta.hasConfirmedTerms) {
          this.errors.meta.hasConfirmedTerms =
            'Please agree to the terms and conditions before completing your purchase.';
        }
      },
      async submitForm() {
        this.isSubmitting = true;
        this.setFormMeta();
        this.clear_errors();

        if (this.form.billing.isSameAsShipping) {
          this.form.billing.firstName = this.form.customer.firstName;
          this.form.billing.lastName = this.form.customer.lastName;
          this.form.billing.address = this.form.customer.address;
          this.form.billing.address2 = this.form.customer.address2;
          this.form.billing.city = this.form.customer.city;
          this.form.billing.country = this.form.customer.country;
          this.form.billing.postcode = this.form.customer.postcode;
          this.form.billing.state = this.form.customer.state;
        }

        try {
          if (
            this.form.payment.paymentType === 'cc' &&
            !this.validateExpiration()
          ) {
            this.isSubmitting = false;
            return;
          }
          const { data } = await axios.post('/checkout', this.form);
          window.location.href = '/checkout/thankyou/' + data.order_id;
        } catch (error) {
          if (!error.response?.data?.errors) {
            // THIS IS NOT A VALIDATION ERROR
            this.errors.message =
              'There was an error submitting this form. Please contact support.';
            return;
          }

          let scrollToId = '';
          const errors = error.response.data.errors;

          errors.forEach((errorGroup) => {
            const errorSection = errorGroup.section;
            const errorKeys = Object.keys(errorGroup.errors);
            errorKeys.forEach((errorKey) => {
              this.errors[errorSection][errorKey] =
                errorGroup.errors[errorKey].join('<br />');
            });

            if (!scrollToId && errorSection !== 'meta') {
              scrollToId = `${errorSection}-information`;
            } else if (
              !scrollToId &&
              errorSection === 'meta' &&
              errorKeys.includes('hasConfirmedTerms')
            ) {
              scrollToId = `${errorSection}-has-confirmed-terms`;
              this.errors.meta.hasConfirmedTerms =
                'Please agree to the terms and conditions before completing your purchase.';
            }
          });

          // SCROLL TO THE SECTION CONTAINING THE ERROR
          const errorElement = document.getElementById(scrollToId);
          errorElement.scrollIntoView({ behavior: 'smooth' });
        } finally {
          this.isSubmitting = false;
        }
      },
      async check_bin(card_number) {
        try {
          const { data } = await axios.post('bin-check', {
            credit_card: card_number,
          });
          this.form.payment.isCreditCard =
            data.success && data.data === 'Credit';
        } catch (err) {
          this.form.payment.isCreditCard = false;
        }
      },
      clear_errors() {
        this.errors = {
          message: '',
          meta: {
            hasConfirmedTerms: '',
          },
          account: {
            email: '',
            password: '',
            passwordConfirmation: '',
          },
          customer: {
            firstName: '',
            lastName: '',
            company: '',
            phone: '',
            address: '',
            address2: '',
            city: '',
            country: '',
            postcode: '',
            state: '',
          },
          billing: {
            firstName: '',
            lastName: '',
            address: '',
            address2: '',
            city: '',
            country: '',
            postcode: '',
            state: '',
          },
          mail: {
            mailType: '',
            zipCode: '',
            shippingRate: '',
          },
          payment: {
            paymentType: '',
            cardNumber: '',
            expiryMonth: '',
            expiryYear: '',
            cvv: '',
            routingNumber: '',
            accountNumber: '',
            accountName: '',
          },
        };
      },
      validateExpiration() {
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        const currentMonthPadded = currentMonth.toString().padStart(2, '0');

        if (currentYear > this.form.payment.expiryYear) {
          this.errors.payment.expiryYear = 'Expired';
          return false;
        }
        if (
          this.form.payment.expiryMonth < currentMonthPadded &&
          currentYear === this.form.payment.expiryYear
        ) {
          this.errors.payment.expiryMonth = 'Expired';
          return false;
        }
        return true;
      },
      backToCart() {
        this.showModal = false;
        window.location.href = '/cart';
      },
    },
  };
</script>

<style lang="scss" scoped>
  .checkout-page {
    width: 100%;

    &__button {
      width: fit-content;
    }

    &__form {
      display: flex;
      flex-direction: column;
      gap: 32px;
      width: 100%;
    }

    &__container-row {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      @include screen-lg {
        flex-direction: row;
        gap: 32px;

        > * {
          width: 50%;
        }
      }
    }

    &__form-container {
      width: 100%;

      &--hideable {
        // I AM USING MAX-HEIGHT TO ANIMATE THE HIDDEN STATE
        // THIS WAY THE HEIGHT OF THIS CONTAINER IS THE HEIGHT OF THE CONTENT
        // BUT WHEN IT IS HIDDEN, IT WILL ANIMATE TO 0 HEIGHT
        opacity: 1;
        max-height: 1000px;
        overflow: hidden;
        transition:
          max-height 300ms ease-in-out,
          opacity 250ms ease-in-out;
      }
      &--hidden {
        opacity: 0;
        max-height: 0;
        transition:
          max-height 300ms ease-in-out,
          opacity 550ms ease-in-out;
      }
    }

    &__loading {
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 999;

      background: var(--itr-white);
      box-shadow: 0px 3px 5px rgba($black, 0.25);
      border-radius: 10px;
      text-align: center;
      width: 300px;

      padding: 0.5rem;

      // I AM SURE THIS ISN'T NEEDED BUT I AM TO TIRED TO LOOK INTO IT
      // TODO: REPLACE THIS LATER
      // @include responsive-padding(all, 0.5, $scale: $spacing-default);
    }
  }

  @media (min-width: 1000px) {
    .order-summary-box-area {
      position: sticky;
      top: 10px;
    }
  }

  .order-summary-box {
    // @include responsive-padding(all, $scale: $spacing-sections);
    background: $blue-mid;
    border-radius: 10px;
    color: $white;
    font-family: $font-secondary;

    & * {
      color: $white;
    }

    h4 {
      margin-bottom: 30px;
    }

    .summary-product {
      display: flex;
      align-items: flex-start;
      margin-bottom: 50px;

      div {
        &:first-child {
          flex-shrink: 0;
          width: 85px;
          text-align: right;
          margin-right: 30px;
        }

        &:last-child {
        }
      }
    }

    .summary-total {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      border-top: 1px solid $blue-light;
      padding-top: 35px;

      div {
        &.total {
          font-size: 25px;
          font-weight: 800;
          margin-top: 15px;
          width: calc(100% - 130px);
        }

        &.total-text {
          width: 120px;
          text-align: right;
        }
      }
    }
  }

  .submit-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__terms {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      position: relative;
    }

    @include screen-lg {
      flex-direction: row;
      align-items: center;
    }
  }

  .terms-error {
    position: absolute;
    bottom: -1.8rem;
    color: var(--other-red);
    font-size: 12px;
    font-weight: 700;
    margin: 0;
    padding: 0 8px;
    min-height: 25px;
    width: 100%;
  }
  .subscription-modal {
    padding: 28px;
    text-align: center;
    margin: auto;
    &__title {
      margin-bottom: 2rem;
      font-weight: 700;
      color: var(--itr-blue);
      @include screen-md {
        font-size: 30px;
      }
    }
    &__subtitle {
      margin-bottom: 1.5rem;
      font-size: 18px;
      font-weight: 700;
      color: var(--itr-charcoal);
    }
    &__button {
      padding: 4px !important;
      margin: 10px 5px 0px 5px;
      width: 250px;
    }
  }
</style>
